import React, { useState, useEffect, useRef } from 'react';
import { Send, MapPin, Mail, Phone, CheckCircle, AlertCircle, ChevronUp } from 'lucide-react';
import { CONTACT_INFO } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Scroll to Top Button
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.scrollY > 400);
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
      aria-label="Scroll to top"
    >
      <ChevronUp size={20} />
    </button>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitStatus) {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        message: formData.message,
      });

      if (response.status === 200) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
      
      if (error.response?.data?.detail) {
        setErrorMessage(error.response.data.detail);
      } else {
        setErrorMessage('Unable to submit form. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-24">
      {/* Hero Section */}
      <section className="section-padding section-champagne relative">
        <div className="absolute top-20 bottom-20 left-8 md:left-16 w-px bg-gradient-to-b from-transparent via-velora-gold/20 to-transparent" />

        <div className="container-luxury">
          <div className="max-w-3xl ml-8 md:ml-12">
            <span 
              className="label-sm mb-8 block animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.2s', animationFillMode: 'forwards', color: 'var(--velora-gold)' }}
            >
              Contact
            </span>
            <h1 
              className="display-lg mb-10 animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
            >
              Request Access
            </h1>
            <div 
              className="divider-gold mb-10 animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }} 
            />
            <p 
              className="body-editorial text-lg animate-fade-in-up opacity-0" 
              style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
            >
              We work with select brands and institutions that understand the value of strategic
              positioning. If that resonates, we'd like to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={sectionRef} className="section-padding section-ivory">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
            {/* Form */}
            <div 
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mb-10 p-8 bg-velora-green-deep/5 border border-velora-green/15">
                  <div className="flex items-start space-x-5">
                    <CheckCircle size={22} style={{ color: 'var(--velora-green)' }} className="flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="heading-md mb-3" style={{ color: 'var(--velora-green-deep)' }}>
                        Message Received
                      </h4>
                      <p className="body-md" style={{ color: 'var(--velora-charcoal-light)' }}>
                        Thank you for reaching out. We typically respond within 48 hours.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-10 p-8 bg-red-50 border border-red-200">
                  <div className="flex items-start space-x-5">
                    <AlertCircle size={22} className="text-red-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="heading-md mb-3 text-red-700">Submission Failed</h4>
                      <p className="body-md text-red-600">
                        {errorMessage || 'Something went wrong. Please try again or contact us directly.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-10">
                <div>
                  <label htmlFor="name" className="label-sm mb-4 block">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-velora-green-deep/15 focus:border-velora-gold focus:outline-none transition-colors duration-400 body-md"
                    placeholder="Your name"
                    style={{ color: 'var(--velora-green-deep)' }}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="label-sm mb-4 block">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-velora-green-deep/15 focus:border-velora-gold focus:outline-none transition-colors duration-400 body-md"
                    placeholder="your@email.com"
                    style={{ color: 'var(--velora-green-deep)' }}
                  />
                </div>

                <div>
                  <label htmlFor="company" className="label-sm mb-4 block">Company / Organization</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-velora-green-deep/15 focus:border-velora-gold focus:outline-none transition-colors duration-400 body-md"
                    placeholder="Your company name"
                    style={{ color: 'var(--velora-green-deep)' }}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="label-sm mb-4 block">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-0 py-4 bg-transparent border-0 border-b border-velora-green-deep/15 focus:border-velora-gold focus:outline-none transition-colors duration-400 body-md resize-none"
                    placeholder="Tell us about your brand and what you're looking to achieve"
                    style={{ color: 'var(--velora-green-deep)' }}
                  />
                </div>

                <a
                  href="https://wa.me/918208377252?text=Hello%20Ashwini%2C%0AI%20came%20across%20Velora%20and%20would%20like%20to%20request%20access%20to%20explore%20a%20potential%20collaboration."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-access btn-access-dark w-full md:w-auto group"
                >
                  <span className="relative z-10">Send Message</span>
                  <Send size={14} className="ml-3 relative z-10" />
                </a>
              </form>
            </div>

            {/* Contact Info */}
            <div 
              className={`lg:pl-12 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <div className="space-y-14">
                <div>
                  <div className="flex items-center space-x-4 mb-5">
                    <Mail size={18} style={{ color: 'var(--velora-gold)' }} />
                    <span className="label-sm">Email</span>
                  </div>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="body-editorial text-lg hover:text-velora-gold transition-colors duration-400"
                    style={{ color: 'var(--velora-green-deep)' }}
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>

                <div>
                  <div className="flex items-center space-x-4 mb-5">
                    <Phone size={18} style={{ color: 'var(--velora-gold)' }} />
                    <span className="label-sm">Phone</span>
                  </div>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="body-editorial text-lg hover:text-velora-gold transition-colors duration-400"
                    style={{ color: 'var(--velora-green-deep)' }}
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>

                <div>
                  <div className="flex items-center space-x-4 mb-5">
                    <MapPin size={18} style={{ color: 'var(--velora-gold)' }} />
                    <span className="label-sm">Office</span>
                  </div>
                  <p className="body-editorial text-lg" style={{ color: 'var(--velora-green-deep)' }}>
                    {CONTACT_INFO.office}
                  </p>
                </div>

                <div className="pt-8">
                  <div className="divider-gold mb-10" />
                  <p className="body-md" style={{ color: 'var(--velora-charcoal-light)' }}>
                    We typically respond within 48 hours. For urgent matters, please indicate so in your message.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="section-padding section-green text-center relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-b from-velora-gold/[0.04] to-transparent rounded-full blur-3xl" />
        </div>
        <div className="container-luxury max-w-3xl relative z-10">
          <h2 className="display-md mb-10" style={{ color: 'var(--velora-ivory)' }}>
            Not every inquiry leads to engagement.
          </h2>
          <p className="body-editorial text-lg" style={{ color: 'var(--velora-champagne)' }}>
            We take on select projects where there is clear alignment between vision, values, and
            ambition. This ensures every engagement receives our full attention and delivers
            exceptional outcomes.
          </p>
        </div>
      </section>

      <ScrollToTop />
    </main>
  );
};

export default ContactPage;
